import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { KEY_RECOIL } from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { selectActiveTab, selectBeatChanges, useReportStore } from '@/entities/report';
import { checkAndProcessBeatData } from '@/entities/beat';
import { checkAndProcessEcgData } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { generateUpdatedBeatsV2 } from './helper';
import type { HrsEventPreviewStripProps } from './hrsEventPreviewStrip';

/**
 * Loads the ECG waveform and beat markers for one HR summary event preview strip. Downloads the strip whenever the event id, its start or the pending beat changes move, overlays those local beat changes on the fetched beats, and clears its stored timeout when the active sub-tab changes.
 *
 * @param props - The event's id, start and stop plus the ECG data map to read from.
 * @returns An object carrying ecgData, beatMarkers and hesBeatStatus.
 */
const useHrsEventPreviewStrip = ({
  id,
  start,
  stop,
  ecgDataMap,
}: Pick<HrsEventPreviewStripProps, 'id' | 'start' | 'stop' | 'ecgDataMap'>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));

  const [ecgData, setEcgData] = useState<any[]>([]);
  const [beatMarkers, setBeatMarkers] = useState<any[]>([]);
  const [hesBeatStatus, setHesBeatStatus] = useState<any[]>([]);

  const timeoutRef = useRef<any>();

  let isActiveQuery: boolean;

  /**
   * Fetches the ECG samples and beat data covering one HR summary preview strip's time window. Computes the sample length from the window and the sampling frequency, awaits the ECG and beat processors, and while the effect is still active stores the samples and the beats merged with pending beat changes; cancelled requests are ignored and other failures logged.
   *
   * @returns A promise resolving once the strip's ECG samples and beat markers are stored.
   */
  const downloadEventStrip = async () => {
    try {
      const startEpoch = moment(start).valueOf();
      const stopEpoch = moment(stop).valueOf();
      const dataLength =
        ((stopEpoch.valueOf() - startEpoch.valueOf()) / 1000) * ecgDataMap.samplingFrequency;
      let ecgData: any = await checkAndProcessEcgData({
        ecgDataMap,
        dataLength,
        momentObject: {
          startMoment: startEpoch,
          stopMoment: stopEpoch,
        },
      });
      // Check and process beat data
      // checkAndProcessBeatData's param type (handlerBeatData.ts) has no dataLength field; this
      // extra property is ignored at runtime - pre-existing.
      let beatData = await checkAndProcessBeatData({
        ecgDataMap,
        dataLength,
        momentObject: {
          startMoment: startEpoch,
          stopMoment: stopEpoch,
        },
      } as any);

      if (isActiveQuery) {
        setEcgData(ecgData);
        if (beatData) {
          const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
            beats: beatData?.beats,
            hesBeatStatus: beatData?.hesBeatStatus,
            beatChanges,
            startEpoch,
            stopEpoch,
          });

          setBeatMarkers(updatedBeats);
          setHesBeatStatus(updatedHesBeatStatus);
        }
      }

      ecgData = null;
      beatData = null;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError(error.message, 'Error');
      }
    }
  };

  useEffect(() => {
    isActiveQuery = true;
    downloadEventStrip();
    return () => {
      isActiveQuery = false;
    };
  }, [id, beatChanges, start]);

  useEffect(
    () => () => {
      // activeTabState (Recoil, Task 10) is disjoint from the string param removeStore expects - pre-existing gap.
      storeTimeout.removeStore(activeButton as any, timeoutRef.current);
    },
    [activeButton],
  );

  return {
    ecgData,
    beatMarkers,
    hesBeatStatus,
  };
};

export default useHrsEventPreviewStrip;
